import { lazy, memo, Suspense, useCallback, useEffect, useState } from 'react';

import EvetroSpinner from '@components/Loader/EvetroSpinner';
import participantDefault from '@constants/participantDefault';
import { loadMyUser } from '@helpers/auth';
import { useApplicationStore } from '@state/application/applicationStore';

const PublicRouter = lazy(() => import('./components/Routing/PublicRouter'));
const DataProvider = lazy(() => import('./state/DataProvider'));

const AUTH_STATES = {
  checking: 'checking',
  authenticated: 'authenticated',
  unauthenticated: 'unauthenticated'
};

const EvetroContainer = () => {
  const [{ userDataFetched, finishedAppLoading }, setAppState] =
    useApplicationStore();

  const [authState, setAuthState] = useState(AUTH_STATES.checking);

  const loadUser = useCallback(
    async (user = null) => {
      const needsFetch = !user?.id;

      if (needsFetch) {
        setAuthState(AUTH_STATES.checking);
      }

      try {
        const resolvedUser = needsFetch ? await loadMyUser() : user;

        if (resolvedUser?.id) {
          setAppState({ me: resolvedUser, userSessionFetched: true });
          setAuthState(AUTH_STATES.authenticated);
          return;
        }
      } catch (error) {
        console.error(error);
        // optionally log in dev mode
      }

      setAppState({ me: participantDefault, userSessionFetched: true });
      setAuthState(AUTH_STATES.unauthenticated);
    },
    [setAppState]
  );

  useEffect(() => {
    loadUser();
  }, []);

  const finishAppLoading = () => {
    setAppState({ finishedAppLoading: true });
  };

  const isAuthenticated = authState === AUTH_STATES.authenticated;
  const isGuest = authState === AUTH_STATES.unauthenticated;

  return (
    <>
      {isGuest && (
        <Suspense fallback={null}>
          <PublicRouter onUserLoggedIn={loadUser} />
        </Suspense>
      )}

      {isAuthenticated && (
        <Suspense fallback={null}>
          <DataProvider />
        </Suspense>
      )}

      {isAuthenticated && !finishedAppLoading && (
        <EvetroSpinner
          onAnimationFinished={finishAppLoading}
          shouldAnimationFinish={userDataFetched}
        />
      )}
    </>
  );
};

export default memo(EvetroContainer);
